import { useCallback, useMemo } from 'react'
import clsx from 'clsx'

import { useModal } from '@/components/Modal'
import { Modal } from '@/ui/Modal'
import { Loader } from '@/ui/Loader'
import { useGetRouteDetails } from '../api/useGetRouteDetails'
import { ROUTES } from '@/router'
import { LinkButton } from '@/ui/Button'
import { useWorkstationSelect } from '@/modules/Workstation/hooks/useWorkstationSelect'
import { RouteDetailsContext } from './context'
import { Details } from './components/Details'
import { Strategy } from './components/Strategy'

import styles from './RouteDetailsModal.module.scss'

type ModalProps = {
    route: string
    workstationNumber: string
    readOnly?: boolean
}

export const RouteDetailsModal = () => {
    const { closeModal, props } = useModal<ModalProps>()
    const { data: details, isLoading } = useGetRouteDetails(props?.route)
    const { handleWorkstationChange } = useWorkstationSelect()

    const disruptions = useMemo(
        () =>
            [...(details?.disruptions || [])]
                .filter((disruption) => disruption.status === 'Live')
                .sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime()),
        [details?.disruptions]
    )

    const strategies = details?.strategies || []

    const theDetails = useMemo(
        () => (details ? { ...details, disruptions, strategies } : null),
        [details, disruptions, strategies]
    )

    const noStrategiesOrDisruptions = disruptions.length === 0 && strategies.length === 0

    const handleViewWorkstation = useCallback(() => {
        if (!props?.workstationNumber) {
            return
        }

        handleWorkstationChange(props.workstationNumber)
        closeModal()
    }, [props?.workstationNumber, handleWorkstationChange, closeModal])

    if (!props) {
        return null
    }

    const readOnly = !!props.readOnly

    return (
        <Modal
            onClose={closeModal}
            className={clsx(styles.modal, { [styles.readOnly]: readOnly && noStrategiesOrDisruptions })}
        >
            {isLoading && (
                <div className={styles.loadingContainer}>
                    <Loader />
                </div>
            )}
            {!isLoading && details && (
                <RouteDetailsContext.Provider value={{ workstationNumber: props.workstationNumber, readOnly }}>
                    <h4 className={styles.head}>Route {props.route}</h4>
                    {theDetails && <Details {...theDetails} />}
                    {noStrategiesOrDisruptions && (
                        <div className={styles.container}>
                            <div className={styles.noDetailsContainer}>No strategies or disruptions for this route</div>
                        </div>
                    )}
                    <Strategy strategies={strategies} route={theDetails?.route || ''} />
                    <div className={styles.footer}>
                        <LinkButton to={ROUTES.HOME} rounded variant="tertiary" onClick={handleViewWorkstation}>
                            View {props.workstationNumber}
                        </LinkButton>
                    </div>
                </RouteDetailsContext.Provider>
            )}
        </Modal>
    )
}
